<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/modules/auth/stores/auth-store'
import IndividualAccountLayout from '@/modules/investing/components/individuals/individual-account-layout.vue'
import IndividualForm from '@/modules/investing/components/individuals/individual-form.vue'
import IndividualLayout from '@/modules/investing/components/individuals/individual-layout.vue'
import { useSkeleton } from '@/modules/core/composables/useSkeleton'
import { scrollToTop } from '@/modules/investing/utils/scroll-to-section'
import { useIndividualStore } from '@/modules/investing/stores/better-individual-store'
import { VButton, VIcon } from '@/modules/shared/components'

const route = useRoute()
const individualStore = useIndividualStore()

///////////////////////////////////////////////////////////////////////////////
// Calls
///////////////////////////////////////////////////////////////////////////////

const individual = computed(() => individualStore.individual)

///////////////////////////////////////////////////////////////////////////////
// Actions
///////////////////////////////////////////////////////////////////////////////

const { individual_id, slug } = route.params as { individual_id: string; slug: string }

const fetch = async () => {
  showSkeleton()
  await individualStore.fetchIndividual(individual_id, { slug })
  hideSkeleton()
}

const submit = async (payload) => {
  await individualStore.updatePersonalInformation(individual_id, payload, { slug })
  await fetch()
  cancelEdit()
}

const confirmChanges = async (payload) => {
  await individualForm.value.submit()
}

///////////////////////////////////////////////////////////////////////////////
// Authorization
///////////////////////////////////////////////////////////////////////////////

const authStore = useAuthStore()
const is_viewing_own_profile = computed(() => individual_id === authStore.current_user.investor_id?.toString())
const isAdmin = computed(() => is_viewing_own_profile.value || authStore.is_site_or_group_admin)

///////////////////////////////////////////////////////////////////////////////
// Main
///////////////////////////////////////////////////////////////////////////////

const { skeleton, hideSkeleton, showSkeleton } = useSkeleton()
const individualForm = ref(null)
const inFormMode = ref(false)

const editForm = () => {
  inFormMode.value = true
}

const cancelEdit = () => {
  individualForm.value.resetForm()
  inFormMode.value = false
  scrollToTop()
}

onMounted(async () => {
  await fetch()
})
</script>

<template>
  <IndividualLayout selectedTab="account">
    <IndividualAccountLayout selectedTab="personal-information">
      <!-- Note: can't use emits because emit can't handle Promise function, hence button's loading state is not working -->
      <IndividualForm ref="individualForm" :individual="individual" :inDisplayMode="!inFormMode" :onSubmit="submit">
        <template #actions>
          <VButton variant="v-white" :click="editForm" v-if="!inFormMode">
            <div class="flex items-center gap-2 stroke-[#3b88af] text-sm">
              Edit
              <VIcon name="Pencil" />
            </div>
          </VButton>
          <VButton variant="v-blue" size="lg" :click="confirmChanges" v-if="inFormMode">Confirm Changes</VButton>
          <VButton variant="v-white" :click="cancelEdit" v-if="inFormMode">
            <div class="flex items-center gap-2 stroke-[#3b88af] text-sm">
              Cancel
              <VIcon name="X" />
            </div>
          </VButton>
        </template>
      </IndividualForm>
    </IndividualAccountLayout>
  </IndividualLayout>
</template>
